import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';

/**
 * Compute recommended post quantities for a given shape and total railing quantity.
 * Total posts always equals n + 1.
 *  - gerade: end=2, corner=0, between=n-1
 *  - lform:  end=2, corner=1, between=n-2
 *  - uform:  end=2, corner=2, between=n-3
 */
function computePosts(shape, n) {
  let corner = 0;
  if (shape === 'lform') corner = 1;
  if (shape === 'uform') corner = 2;
  const end = n > 0 ? 2 : 0;
  const between = Math.max(0, n - 1 - corner);
  return { end, corner, between };
}



class RailingSuggestionComponent extends Component {
  openRailingModal(event) {
    const btn = event.target.closest('[data-shape]');
    if (!btn) return;
    const modal = this.querySelector('.railing-modal');
    if (!modal) return;

    this._applyShape(modal, btn.dataset.shape);
    this._clearMismatch(modal);
    modal.hidden = false;
  }

  closeModal() {
    this.querySelectorAll('.railing-modal:not([hidden])').forEach((m) => {
      m.classList.add('is-closing');
      m.addEventListener('animationend', () => {
        m.classList.remove('is-closing');
        m.hidden = true;
      }, { once: true });
    });
  }

  selectShape(event) {
    const btn = event.target.closest('[data-shape-select]');
    if (!btn) return;
    const modal = btn.closest('.railing-modal');
    if (!modal) return;
    this._applyShape(modal, btn.dataset.shapeSelect);
    this._clearMismatch(modal);
  }

  /** Set the active shape, recompute post quantities and update UI state. */
  _applyShape(modal, shape) {
    modal.dataset.shape = shape;

    // Highlight the active shape selector button
    modal.querySelectorAll('[data-shape-select]').forEach((el) => {
      el.classList.toggle('is-active', el.dataset.shapeSelect === shape);
    });

    let totalRailingQty = 0;
    modal.querySelectorAll('[data-role="railing"] .railing-modal__qty-input').forEach((input) => {
      totalRailingQty += Number(input.value) || 0;
    });

    const { end, corner, between } = computePosts(shape, totalRailingQty);
    this._setPostQty(modal, 'end', end);
    this._setPostQty(modal, 'corner', corner);
    this._setPostQty(modal, 'between', between);
  }

  _setPostQty(modal, role, qty) {
    // Hide the whole post block (incl. its divider) when the shape doesn't need this post type
    const block = modal.querySelector('[data-post-block="' + role + '"]');
    if (block) block.hidden = qty <= 0;
    const input = modal.querySelector('[data-role="' + role + '"] .railing-modal__qty-input');
    if (input) input.value = qty;
    const label = modal.querySelector('[data-role="' + role + '"] .railing-modal__suggested-qty');
    if (label) label.textContent = qty;
  }

  changeQty(event) {
    const btn = event.target.closest('[data-qty-change]');
    if (!btn) return;
    const itemEl = btn.closest('.railing-modal__item');
    const input = btn.closest('.railing-modal__qty')?.querySelector('.railing-modal__qty-input');
    if (!input) return;

    const isRailing = itemEl?.dataset.role === 'railing';
    const min = isRailing ? 1 : 0;
    const next = Math.max(min, (Number(input.value) || 0) + Number(btn.dataset.qtyChange));
    input.value = next;

    const modal = btn.closest('.railing-modal');
    if (!modal) return;

    if (isRailing) {
      // Recompute posts from the current shape and the new railing total
      this._applyShape(modal, modal.dataset.shape || 'gerade');
      this._clearMismatch(modal);
    } else {
      this._updateMismatch(modal);
    }
  }

  _clearMismatch(modal) {
    modal.querySelectorAll('.railing-modal__qty-input.is-mismatched').forEach((el) => el.classList.remove('is-mismatched'));
    modal.querySelectorAll('.railing-modal__mismatch').forEach((el) => { el.hidden = true; });
  }

  _updateMismatch(modal) {
    let totalRailingQty = 0;
    modal.querySelectorAll('[data-role="railing"] .railing-modal__qty-input').forEach((inp) => {
      totalRailingQty += Number(inp.value) || 0;
    });

    const expected = computePosts(modal.dataset.shape || 'gerade', totalRailingQty);
    ['end', 'corner', 'between'].forEach((role) => {
      const block = modal.querySelector('[data-post-block="' + role + '"]');
      const input = modal.querySelector('[data-role="' + role + '"] .railing-modal__qty-input');
      if (!input) return;
      // A post type that the shape doesn't need (expected 0) stays hidden and never mismatches
      if (expected[role] <= 0) {
        if (block) block.hidden = true;
        input.classList.remove('is-mismatched');
        const warn = modal.querySelector('[data-role="' + role + '"] .railing-modal__mismatch');
        if (warn) warn.hidden = true;
        return;
      }
      if (block) block.hidden = false;
      const mismatch = Number(input.value) !== expected[role];
      input.classList.toggle('is-mismatched', mismatch);
      const warn = modal.querySelector('[data-role="' + role + '"] .railing-modal__mismatch');
      if (warn) warn.hidden = !mismatch;
    });
  }

  async handleUpdateToMatch(event) {
    const btn = event.target.closest('button');
    if (!btn || btn.disabled) return;
    const modal = btn.closest('.railing-modal');
    if (!modal) return;

    btn.disabled = true;

    const shape = modal.dataset.shape || 'gerade';
    const groupToken = String(modal.dataset.railingGroup || '');

    const postTypes = [
      { role: 'end', variantId: Number(modal.dataset.endVariantId) || 0, deliveryTime: modal.dataset.endDeliveryTime ?? '' },
      { role: 'corner', variantId: Number(modal.dataset.cornerVariantId) || 0, deliveryTime: modal.dataset.cornerDeliveryTime ?? '' },
      { role: 'between', variantId: Number(modal.dataset.betweenVariantId) || 0, deliveryTime: modal.dataset.betweenDeliveryTime ?? '' },
    ];

    const sections = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el.dataset.sectionId) sections.add(el.dataset.sectionId);
    });
    const sectionsParam = Array.from(sections).join(',');

    // A stable railing line key for the final re-render request
    const railingKeys = Array.from(modal.querySelectorAll('[data-role="railing"]'))
      .map((el) => el.dataset.itemKey)
      .filter(Boolean);

    try {
      // 1. Fetch current cart up front to find the existing post lines for this group
      const cartData = await fetch('/cart.js').then((r) => r.json());

      const postVariantIds = postTypes.map((p) => p.variantId).filter(Boolean);
      const existingPostLines = cartData.items.filter((ci) => {
        if (!postVariantIds.includes(ci.variant_id)) return false;
        const fenceGroup = String(ci.properties?._railing_group ?? '');
        // Match this exact group's tag, but ALSO sweep up untagged (blank) lines of
        // these same post variant ids — these post variants are specific to THIS
        // railing product, so a blank tag can only be a stray leftover from before
        // proper tagging (e.g. an older bug, or added standalone) — not ambiguous
        // like the shared screws product is for fences. Without this, switching
        // shape would leave the previous shape's posts behind untouched.
        return fenceGroup === groupToken || fenceGroup === '';
      });

      // 2. Remove ALL existing group posts AND apply the railing quantity change
      //    together in ONE bulk /cart/update.js request instead of several
      //    sequential /cart/change.js calls. Firing them one-by-one previously
      //    produced a `400 Bad Request` on one of the removals (confirmed via
      //    devtools) — Shopify/cart-validation logic was rejecting an
      //    intermediate, partially-updated cart state. Committing the whole
      //    batch as a single atomic request means only the final, consistent
      //    state is ever evaluated, and there's no in-between state to reject.
      const updates = /** @type {Record<string, number>} */ ({});
      for (const railingEl of modal.querySelectorAll('[data-role="railing"]')) {
        const railingInput = railingEl.querySelector('.railing-modal__qty-input');
        const originalQty = Number(railingInput?.dataset.originalQty) || 0;
        const newQty = Number(railingInput?.value) || 0;
        const itemKey = railingEl.dataset.itemKey;
        if (itemKey && newQty > 0 && newQty !== originalQty) {
          updates[itemKey] = newQty;
        }
      }
      for (const line of existingPostLines) {
        updates[line.key] = 0;
      }

      if (Object.keys(updates).length > 0) {
        let updateResponse = await fetch(Theme.routes.cart_update_url, fetchConfig('json', {
          body: JSON.stringify({ updates }),
        }));
        if (!updateResponse.ok) {
          updateResponse = await fetch(Theme.routes.cart_update_url, fetchConfig('json', {
            body: JSON.stringify({ updates }),
          }));
        }
        if (!updateResponse.ok) {
          console.error('[railing-suggestion] bulk cart update failed', updates, updateResponse.status);
        }
      }

      // 3. Build the fresh post items for the chosen shape
      const addItems = [];
      for (const { role, variantId, deliveryTime } of postTypes) {
        if (!variantId) continue;
        const qty = Number(modal.querySelector('[data-role="' + role + '"] .railing-modal__qty-input')?.value) || 0;
        if (qty > 0) {
          addItems.push({
            id: variantId,
            quantity: qty,
            properties: { _railing_group: groupToken, _railing_shape: shape, _delivery_time: deliveryTime },
          });
        }
      }

      // 4. Add fresh items (silently — we verify/re-render below)
      if (addItems.length > 0) {
        const addBody = JSON.stringify({ items: addItems, sections_url: window.location.pathname });
        await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body: addBody }));
      }

      // 5. Safety net: if step 2's bulk removal was rejected/partial for any
      //    reason, we could now have TWO lines for the same post role/variant
      //    in this group instead of one. Verify against a fresh cart fetch and
      //    merge any duplicates into a single line before rendering, so the
      //    customer never sees a doubled-up "End"/"Corner"/"Between" row.
      //    IMPORTANT: use the SAME relaxed match as existingPostLines above
      //    (exact groupToken OR blank) — a leftover line that failed removal
      //    is often still blank-tagged (never got its properties rewritten),
      //    so requiring an exact groupToken match here would make it invisible
      //    to the merge and defeat the whole safety net.
      const finalCartData = await fetch('/cart.js').then((r) => r.json());
      const finalPostLines = finalCartData.items.filter((ci) => {
        if (!postVariantIds.includes(ci.variant_id)) return false;
        const fenceGroup = String(ci.properties?._railing_group ?? '');
        return fenceGroup === groupToken || fenceGroup === '';
      });

      /** @type {Map<number, typeof finalPostLines>} */
      const linesByVariant = new Map();
      for (const line of finalPostLines) {
        const list = linesByVariant.get(line.variant_id) ?? [];
        list.push(line);
        linesByVariant.set(line.variant_id, list);
      }

      for (const lines of linesByVariant.values()) {
        if (lines.length < 2) continue;
        // Prefer keeping the line that's already correctly tagged for THIS group
        // and shape — a stale/blank leftover line should be the one merged away,
        // not the freshly-tagged one, otherwise the surviving line would still
        // carry the wrong (or missing) _railing_group/_railing_shape properties.
        const sorted = [...lines].sort((a, b) => {
          const score = (l) => {
            const group = String(l.properties?._railing_group ?? '');
            const shapeMatch = l.properties?._railing_shape === shape;
            if (group === groupToken && shapeMatch) return 2;
            if (group === groupToken) return 1;
            return 0;
          };
          return score(b) - score(a);
        });
        const [keep, ...extra] = sorted;
        const mergedQty = lines.reduce((sum, l) => sum + l.quantity, 0);
        if (mergedQty !== keep.quantity) {
          await fetch(Theme.routes.cart_change_url, fetchConfig('json', {
            body: JSON.stringify({ id: keep.key, quantity: mergedQty }),
          }));
        }
        for (const line of extra) {
          await fetch(Theme.routes.cart_change_url, fetchConfig('json', {
            body: JSON.stringify({ id: line.key, quantity: 0 }),
          }));
        }
      }

      // 6. Trigger the final section re-render (works whether we added items,
      //    deduped some, or neither — a same-quantity "change" is a safe no-op).
      const triggerKey = finalPostLines[0]?.key ?? railingKeys[0];
      if (triggerKey) {
        const refItem = finalCartData.items.find((ci) => ci.key === triggerKey);
        const refQty = refItem ? refItem.quantity : 1;
        const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', {
          body: JSON.stringify({ id: triggerKey, quantity: refQty, sections: sectionsParam, sections_url: window.location.pathname }),
        }));
        const cart = await response.json();
        this.closeModal();
        this.dispatchEvent(new CartAddEvent(cart, this.id, { source: 'railing-suggestion-component', itemCount: cart.item_count, sections: cart.sections }));
        return;
      }

      this.closeModal();
    } catch (error) {
      console.error('[railing-suggestion] update to match failed', error);
    } finally {
      btn.disabled = false;
    }
  }
}

if (!customElements.get('railing-suggestion-component')) {
  customElements.define('railing-suggestion-component', RailingSuggestionComponent);
}
